import { ArrowLeft, Check, ChevronRight, LocateFixed } from "lucide-react";
import { FormEvent, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Brand } from "@/components/brand";
import { api, csrf } from "./api";

function errorMessage(error: unknown) {
  if (typeof error === "object" && error && "response" in error) {
    const response = (
      error as {
        response?: {
          data?: { message?: string; errors?: Record<string, string[]> };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      Object.values(response?.data?.errors ?? {})[0]?.[0] ??
      "Something went wrong."
    );
  }
  return "The server could not be reached.";
}

export function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await csrf();
      await api.post("/auth/login", {
        email: form.get("email"),
        password: form.get("password"),
      });
      navigate("/");
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  }
  return (
    <PublicFrame>
      <p className="eyebrow">Welcome back</p>
      <h1>Pick up where your paths crossed.</h1>
      <p className="muted">
        Your conversations and private plans stay tied to this account.
      </p>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </div>
        <button className="button button-primary button-block" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p>
        <Link to="/forgot-password">Forgot your password?</Link>
      </p>
      <p className="muted">
        New here?{" "}
        <Link to="/register" style={{ color: "var(--color-accent-primary)" }}>
          Create an account
        </Link>
      </p>
    </PublicFrame>
  );
}

export function ForgotPasswordPage() {
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await csrf();
    await api.post("/auth/forgot-password", { email: form.get("email") });
    setMessage("If that account exists, a reset link is on its way.");
  }
  return (
    <PublicFrame>
      <p className="eyebrow">Account recovery</p>
      <h1>Reset your password.</h1>
      <p className="muted">We’ll send a private, time-limited link.</p>
      <form onSubmit={submit}>
        <label className="field">
          <span>Email</span>
          <input name="email" type="email" required />
        </label>
        <button className="button button-primary button-block">
          Send reset link
        </button>
      </form>
      {message && <p role="status">{message}</p>}
      <Link to="/login">Return to sign in</Link>
    </PublicFrame>
  );
}

export function ResetPasswordPage() {
  const { token = "" } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password"));
    try {
      await csrf();
      await api.post("/auth/reset-password", {
        token,
        email: search.get("email"),
        password,
        password_confirmation: password,
      });
      navigate("/login");
    } catch (value) {
      setError(errorMessage(value));
    }
  }
  return (
    <PublicFrame>
      <p className="eyebrow">Secure recovery</p>
      <h1>Choose a new password.</h1>
      <form onSubmit={submit}>
        <label className="field">
          <span>New password</span>
          <input name="password" type="password" minLength={12} required />
        </label>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button className="button button-primary button-block">
          Update password
        </button>
      </form>
    </PublicFrame>
  );
}

type Registration = {
  email: string;
  password: string;
  display_name: string;
  account_type: "single" | "shared_couple";
  date_of_birth: string;
  gender: string;
  locality: string;
  postcode: string;
  accepted: boolean;
};
export function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<Registration>({
    email: "",
    password: "",
    display_name: "",
    account_type: "single",
    date_of_birth: "",
    gender: "",
    locality: "Brisbane",
    postcode: "4000",
    accepted: false,
  });
  const update = (key: keyof Registration, value: string | boolean) =>
    setData((current) => ({ ...current, [key]: value }));
  async function submit() {
    setBusy(true);
    setError("");
    try {
      await csrf();
      await api.post("/auth/register", {
        ...data,
        password_confirmation: data.password,
        seeking: ["Singles", "Couples"],
        region: "Queensland",
        country_code: "AU",
        accepted_terms: data.accepted,
        accepted_privacy: data.accepted,
        accepted_guidelines: data.accepted,
        adult_declaration: data.accepted,
      });
      navigate("/");
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  }
  function detect() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => update("locality", "Brisbane (location detected)"),
      () =>
        setError(
          "Location permission was not granted. Brisbane remains selected.",
        ),
      { enableHighAccuracy: false, timeout: 8000 },
    );
  }
  return (
    <PublicFrame>
      <Link className="text-action" to="/login">
        <ArrowLeft />
      </Link>
      <p className="eyebrow">Join Krosspaths · {step + 1}/3</p>
      {step === 0 && (
        <>
          <h1>First, confirm who you are.</h1>
          <div className="choice-grid">
            <button
              className={`choice ${data.account_type === "single" ? "selected" : ""}`}
              onClick={() => update("account_type", "single")}
            >
              Single account
            </button>
            <button
              className={`choice ${data.account_type === "shared_couple" ? "selected" : ""}`}
              onClick={() => update("account_type", "shared_couple")}
            >
              Shared Couple
            </button>
          </div>
          <div className="field">
            <label htmlFor="name">Display name</label>
            <input
              id="name"
              value={data.display_name}
              onChange={(e) => update("display_name", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="dob">Date of birth</label>
            <input
              id="dob"
              type="date"
              value={data.date_of_birth}
              onChange={(e) => update("date_of_birth", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="gender">Gender identity</label>
            <input
              id="gender"
              value={data.gender}
              onChange={(e) => update("gender", e.target.value)}
            />
          </div>
        </>
      )}
      {step === 1 && (
        <>
          <h1>Keep your real location private.</h1>
          <p className="muted">
            Your account location powers nearby discovery. Posts and Dates each
            choose what other members can see.
          </p>
          <div className="field">
            <label htmlFor="locality">Home city or suburb</label>
            <input
              id="locality"
              value={data.locality}
              onChange={(e) => update("locality", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="postcode">Postcode</label>
            <input
              id="postcode"
              value={data.postcode}
              onChange={(e) => update("postcode", e.target.value)}
            />
          </div>
          <button className="button button-secondary" onClick={detect}>
            <LocateFixed size={18} /> Detect my location
          </button>
          <div className="privacy-preview" style={{ marginTop: "1rem" }}>
            Your home address is never public by default.
          </div>
        </>
      )}
      {step === 2 && (
        <>
          <h1>Secure your account.</h1>
          <div className="field">
            <label htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              value={data.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="reg-password">Password</label>
            <input
              id="reg-password"
              type="password"
              value={data.password}
              onChange={(e) => update("password", e.target.value)}
              minLength={12}
            />
            <small className="muted">At least 12 characters</small>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={data.accepted}
              onChange={(e) => update("accepted", e.target.checked)}
            />
            <span>
              I am 18 or older and accept the Terms, Privacy Policy, and
              Community Guidelines.
            </span>
          </label>
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2rem",
        }}
      >
        <button
          className="button button-quiet"
          disabled={step === 0}
          onClick={() => setStep((value) => value - 1)}
        >
          Back
        </button>
        {step < 2 ? (
          <button
            className="button button-primary"
            onClick={() => setStep((value) => value + 1)}
          >
            Continue <ChevronRight />
          </button>
        ) : (
          <button
            className="button button-primary"
            disabled={busy || !data.accepted}
            onClick={submit}
          >
            {busy ? (
              "Creating…"
            ) : (
              <>
                <Check /> Create account
              </>
            )}
          </button>
        )}
      </div>
    </PublicFrame>
  );
}

function PublicFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth-layout">
      <section className="auth-intro">
        <p className="eyebrow">Private · local · on your terms</p>
        <h2>Make plans. Meet people.</h2>
      </section>
      <section className="auth-panel">
        <Brand />
        {children}
      </section>
    </main>
  );
}
